import { tr } from 'i18n';
import { useTranslation as useInterfaceLanguage } from 'react-i18next';
import { getErpErrorMessage } from "service/ErpService";
import React, { useCallback, useEffect, useState } from "react";
import { Search, RefreshCw, History, LoaderCircle , ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from "lucide-react";
import { getAuditEvents } from "service/ErpService";
import { DateTime, StatusBadge } from "components/Erp/Display";
import { SectionHeading, EmptyState } from "components/Erp/Presentation";
import { ACCESS_LABELS } from "utils/access";
import s from "../Management.module.scss";

export const AUDIT_SECTIONS = [
  ["auth", "Вход и безопасность"],
  ["guards", "Охрана"],
  ["top_bases", "ТОП-базы"],
  ["chpps", "ТЭЦ"],
  ["private_points", "Социальные объекты"],
  ["loads", "Погрузки"],
  ["drivers", "Водители"],
  ["users", "Пользователи"],
  ["base_requests", "Заявки на базы"],
  ["confirmations", "Подтверждения"],
  ["documents", "Документы"],
];
const ACTIONS = {
  register: "Регистрация",
  registration: "Регистрация",
  login: "Вход в систему",
  logout: "Выход из системы",
  login_failed: "Неудачная попытка входа",
  set_pin: "Создание PIN",
  pin_set: "Создание PIN",
  create: "Создание",
  update: "Изменение",
  delete: "Удаление",
  approve: "Подтверждение",
  reject: "Отказ",
  block: "Блокировка",
  unblock: "Разблокировка",
  reset_pin: "Сброс PIN",
  pin_reset: "Сброс PIN",
  inn_change: "Смена ИНН водителя",
  driver_assign: "Назначение водителя",
  driver_release: "Освобождение водителя",
  base_assign: "Назначение базы",
  base_change: "Смена базы",
  base_remove: "Снятие с базы",
  operator_inn_change: "Смена ИНН оператора",
  operator_reassign: "Переназначение оператора базы",
  guard_inn_change: "Смена ИНН охраны",
  entry: "Вход",
  exit: "Выход",
  departure: "Выход из Кара-Кече",
  arrival: "Прибытие на базу",
};
const FIELD_LABELS = {
  vehicle_number: "Номер автомобиля",
  vehicle_model: "Модель автомобиля",
  phone: "Телефон",
  number_phone: "Телефон",
  first_name: "Имя",
  surname: "Фамилия",
  last_name: "Отчество",
  name: "Название",
  status: "Статус",
  is_active: "Активность",
  top_base: "ТОП-база",
  chpp: "ТЭЦ",
  private_point: "Социальный объект",
  new_top_base: "Новая ТОП-база",
  new_chpp: "Новая ТЭЦ",
  new_private_point: "Новый социальный объект",
  employee: "Сотрудник",
  driver: "Водитель",
  request_type: "Направление заявки",
  rejection_reason: "Причина отказа",
  notes: "Заметки",
  email: "Электронная почта",
  username: "ИНН / логин",
  iin: "ИНН в паспортных данных",
  registration_inn: "ИНН в регистрационной заявке",
  is_superuser: "Полный доступ",
  is_report_of: "Только отчёты",
  erp_access: "Разрешения",
  grants: "Разрешения",
  report_only: "Просмотр отчётов без изменений",
  pass_expiry_days: "Срок пропуска",
  pass_expiry_date: "Пропуск действует до",
  pass_loads_limit: "Лимит погрузок",
  pass_loads_count: "Погрузок по пропуску",
  time_a: "Вход в Кара-Кече",
  time_a_exit: "Выход из Кара-Кече",
  time_b: "Прибытие на базу",
  weight: "Вес",
  geo: "Координаты",
  photo: "Фотография",
  created_at: "Создано",
  updated_at: "Изменено",
  reviewed_at: "Рассмотрено",
  middle_name: "Отчество",
  inn: "ИНН",
  photo_vehicle: "Фото автомобиля",
  passport_front: "Паспорт — лицевая сторона",
  passport_back: "Паспорт — обратная сторона",
  license_front: "Удостоверение — лицевая сторона",
  license_back: "Удостоверение — обратная сторона",
  tech_passport_front: "Техпаспорт — лицевая сторона",
  tech_passport_back: "Техпаспорт — обратная сторона",
  field_name: "Тип документа",
  field_label: "Документ",
  old_file: "Предыдущий документ",
  new_file: "Новый документ",
  uid: "Идентификатор пропуска",
  qrcode_scr: "Данные QR-пропуска",
  qrcode_img: "QR-пропуск",
  address: "Адрес",
  branch: "Филиал",
  guard: "Охранник",
  operator: "Оператор",
  location: "Объект",
  user: "Учётная запись",
  erp_role: "Роль пользователя",
  user_type: "Группа пользователя",
  is_staff: "Доступ администратора",
  is_report_only: "Только отчёты",
  is_personal_kyrgyzkomur: "Сотрудник Кыргызкомур",
  result: "Результат",
  reason: "Причина",
  detail: "Результат операции",
  imeag: "Фото профиля",
  image: "Фотография",
  data_of_birth: "Дата рождения",
  gender: "Пол",
  citizenship: "Гражданство",
  nationality: "Национальность",
};
const MODEL_LABELS = {
  User: "Пользователь",
  ERPAccess: "Доступ пользователя",
  Branch: "Филиал",
  Driver: "Водитель",
  Employee: "Пользователь",
  Guard: "Охранник",
  TopBase: "ТОП-база",
  CHPP: "ТЭЦ",
  PrivatePoint: "Социальный объект",
  TopBaseOperator: "Оператор",
  CHPPOperator: "Оператор ТЭЦ",
  PrivatePointOperator: "Оператор социального объекта",
  BaseRequest: "Заявка на базу",
  DriverChecked: "Регистрация водителя",
  DocChangeRequest: "Изменение документа",
  Load: "Погрузка",
  TopBaseBranch: "Филиал",
  CHPPBranch: "Филиал ТЭЦ",
  PrivatePointBranch: "Филиал социального объекта",
};
const displayValue = (value, key = "") =>
  value == null
    ? "—"
    : typeof value === "boolean"
      ? value
        ? tr("Да")
        : tr("Нет")
      : typeof value === "object"
        ? Array.isArray(value)
          ? value.map((item) => displayValue(item)).join(", ")
          : ["erp_access", "grants"].includes(key)
            ? Object.entries(value)
                .filter(([, enabled]) => enabled)
                .map(([grant]) => ACCESS_LABELS[grant] || FIELD_LABELS[grant] || grant)
                .join(", ") || tr("Нет разрешений")
            : Object.entries(value)
                .map(([field, item]) => `${FIELD_LABELS[field] || ACCESS_LABELS[field] || field}: ${displayValue(item, field)}`)
                .join(", ")
        : (key === "user_type"
            ? { E: tr("Сотрудник"), S: tr("Дополнительный профиль"), OTHER: tr("Другой профиль") }[value]
            : key === "gender"
              ? { M: tr("Мужской"), F: tr("Женский"), OTHER: tr("Другой") }[value]
              : undefined) || STATUS_LABELS[value] || String(value);
const STATUS_LABELS = {
  pending: "На рассмотрении",
  approved: "Подтверждено",
  rejected: "Отклонено",
  join: "Вход",
  leave: "Выход",
  superuser: "Суперпользователь",
  administrator: "Администратор",
  reporter: "Только отчёты",
  user: "Пользователь",
  employee: "Сотрудник Кыргызкомур",
  driver: "Водитель",
  operator: "Оператор базы",
  base_operator: "Оператор базы",
  guard: "Охрана",
  success: "Успешно",
  failed: "Ошибка",
};
const SYSTEM_FIELDS = new Set([
  "id", "pk", "created_at", "updated_at", "date_joined", "last_login",
  "create_date", "createdate", "unique_code_user", "is_online",
  "menu_navigate_position", "qrcode_scr", "qrcode_img",
]);
const FILE_FIELDS = new Set([
  "photo", "image", "imeag", "photo_vehicle", "passport_front", "passport_back",
  "license_front", "license_back", "tech_passport_front", "tech_passport_back",
  "old_file", "new_file", "document", "file",
]);
const PRIORITY_FIELDS = new Set([
  "erp_role", "erp_access", "grants", "is_superuser", "is_staff", "is_active",
  "is_report_of", "is_report_only", "report_only", "user_type", "status", "result",
  "rejection_reason", "reason", "request_type", "vehicle_number", "vehicle_model",
]);
const valuesOf = (change) => {
  if (!change || typeof change !== "object" || Array.isArray(change)) return [null, change];
  return ["before" in change ? change.before : change.old, "after" in change ? change.after : change.new];
};
const defaultImage = (value) => !value || /(?:^|[/\\])(?:profile|default|placeholder)\.(?:png|jpe?g|webp)(?:[?#]|$)/i.test(String(value));
const fileName = (value) => {
  const name = String(value).split(/[?#]/)[0].split(/[/\\]/).pop();
  try { return decodeURIComponent(name); } catch { return name; }
};
export function Changes({ changes, objectType = "", section = "" }) {
  useInterfaceLanguage();
  const [expanded, setExpanded] = useState(false);
  const model = objectType.split(".").pop();
  const registration = section === "confirmations" || model === "DriverChecked";
  const entries = Object.entries(changes || {})
    .filter(([key, change]) => {
      if (SYSTEM_FIELDS.has(key) || /_(?:ru|ky|kg|en)$/.test(key)) return false;
      if (["imeag", "image"].includes(key) && valuesOf(change).every(defaultImage)) return false;
      return true;
    })
    .sort(([a], [b]) => Number(PRIORITY_FIELDS.has(b)) - Number(PRIORITY_FIELDS.has(a)));
  const formatted = (key, value) => {
    if (value && (key.endsWith("_at") || ["time_a", "time_a_exit", "time_b", "pass_expiry_date", "data_of_birth"].includes(key))) {
      return <DateTime value={value} />;
    }
    if (typeof value === "string" && (FILE_FIELDS.has(key) || /\.(?:jpe?g|png|webp|gif|heic|heif|pdf|docx?|xlsx?|csv)(?:[?#]|$)/i.test(value))) {
      return <span title={tr(value)} style={{ overflowWrap: "anywhere" }}>{tr(fileName(value) || tr("Файл"))}</span>;
    }
    return displayValue(value, key);
  };
  if (!entries.length) return null;
  return (
    <div className={s.changes}>
      {(expanded ? entries : entries.slice(0, 5)).map(([key, change]) => {
        const [before, after] = valuesOf(change);
        const label = key === "last_name" && (registration || model === "User") ? tr("Фамилия") : FIELD_LABELS[key];
        return (
          <div key={key} style={{ lineHeight: 1.45 }}>
            <strong>
              {tr(label || ACCESS_LABELS[key] || key.replace(/_/g, " "))}
            </strong>
            : {before == null || before === "" ? formatted(key, after) : <>{formatted(key, before)} <span aria-label={tr("изменено на")}>→</span> {formatted(key, after)}</>}
          </div>
        );
      })}
      {tr(entries.length > 5 && <button type="button" aria-expanded={expanded} onClick={() => setExpanded(!expanded)} className={`${s.button} ${s.compactButton}`} style={{ justifySelf: "start" }}>
        {expanded ? <ChevronUp size={13} aria-hidden="true"/> : <ChevronDown size={13} aria-hidden="true"/>}
        {expanded ? tr("Свернуть изменения") : tr("Показать все изменения ({{value0}})", { value0: entries.length })}
      </button>)}
    </div>
  );
}
export default function AuditPanel({
  section = "",
  objectId = "",
  objectType = "",
  actorId = "",
}) {
  useInterfaceLanguage();
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState(section);
  const [object, setObject] = useState(objectId);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setFilter(section);
    setObject(objectId);
    setPage(1);
  }, [section, objectId]);
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await getAuditEvents({
        section: filter || undefined,
        object_id: object || undefined,
        object_type: objectType || undefined,
        user: actorId || undefined,
        search: query || undefined,
        page,
        page_size: 25,
      });
      setRecords(data.results || data || []);
      setTotal(data.count ?? (data.results || data || []).length);
    } catch (e) {
      setError(getErpErrorMessage(e, tr("Не удалось загрузить журнал.")));
    } finally {
      setLoading(false);
    }
  }, [filter, object, objectType, actorId, query, page]);
  useEffect(() => {
    load();
  }, [load]);
  return (
    <section className={s.panel}>
      <SectionHeading icon={History} title={tr("Логирование")} description={tr("Действия пользователей, время изменений и состояние до и после операции.")}
        actions={<button type="button" className={s.button} onClick={load}>
          <RefreshCw size={14} />{tr("Обновить")}</button>} />
      <form
        className={s.toolbar}
        onSubmit={(e) => {
          e.preventDefault();
          setQuery(search.trim());
          setPage(1);
        }}
      >
        <select
          aria-label={tr("Раздел журнала")}
          value={filter}
          onChange={(e) => {
            setFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">{tr("Все разделы")}</option>
          {AUDIT_SECTIONS.map(([key, label]) => (
            <option value={key} key={key}>
              {tr(label)}
            </option>
          ))}
        </select>
        <input
          value={object}
          aria-label={tr("Номер объекта")}
          placeholder={tr("Номер объекта")}
          onChange={(e) => {
            setObject(e.target.value);
            setPage(1);
          }}
        />
        <input
          value={search}
          aria-label={tr("Поиск в журнале")}
          placeholder={tr("Пользователь или действие")}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button className={s.button}>
          <Search size={14} />{tr("Найти")}</button>
      </form>
      {tr(error && (
        <div role="alert" className={s.error}>
          {tr(error)}
        </div>
      ))}
      {loading ? (
        <EmptyState icon={LoaderCircle} title={tr("Загрузка журнала…")} compact />
      ) : !records.length && error ? null : !records.length ? (
        <EmptyState icon={History} title={tr("Действий по выбранным условиям нет")} description={tr("Измените фильтры или поиск. Новые события будут появляться автоматически.")} compact />
      ) : (
        <div className={s.tableWrap}>
          <table className={s.table}>
            <thead>
              <tr>
                <th>{tr("Время")}</th>
                <th>{tr("Пользователь")}</th>
                <th>{tr("Раздел / объект")}</th>
                <th>{tr("Действие")}</th>
                <th>{tr("Подробности")}</th>
              </tr>
            </thead>
            <tbody>
              {records.map((item) => (
                <tr key={item.id}>
                  <td data-label={tr("Время")}>
                    <DateTime value={item.created_at} />
                  </td>
                  <td data-label={tr("Пользователь")}>
                    <strong>{tr(item.user_name || tr("Система"))}</strong>
                  </td>
                  <td data-label={tr("Раздел / объект")}>
                    {tr(AUDIT_SECTIONS.find(
                      ([key]) => key === item.section,
                    )?.[1] || item.section)}
                    <div className={s.muted}>
                      {tr(MODEL_LABELS[item.object_type?.split(".").pop()] ||
                        tr("Объект"))}{" "}
                      #{tr(item.object_id)}
                    </div>
                  </td>
                  <td data-label={tr("Действие")}>
                    <StatusBadge>
                      {tr(ACTIONS[item.action_display] || item.action_display ||
                        ACTIONS[item.action] ||
                        item.action)}
                    </StatusBadge>
                  </td>
                  <td data-label={tr("Подробности")}>
                    {tr(item.description)}
                    <Changes changes={item.changes} objectType={item.object_type} section={item.section} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className={s.pagination}>
        <span>{tr("Записей: ")}{tr(total)}{tr(" · Страница ")}{tr(page)}
        </span>
        <button
          className={s.button}
          disabled={page <= 1 || loading}
          onClick={() => setPage((p) => p - 1)}
        ><ChevronLeft size={14} aria-hidden="true"/>{tr("Назад")}</button>
        <button
          className={s.button}
          disabled={page * 25 >= total || loading}
          onClick={() => setPage((p) => p + 1)}
        ><ChevronRight size={14} aria-hidden="true"/>{tr("Далее")}</button>
      </div>
    </section>
  );
}
